import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { firstValueFrom } from 'rxjs';

import { PageHeader } from '@core/components/page-header/page-header';
import { ServiceResponseV1 } from '@core/models/apis';
import { OrderAdminListQuery, OrderWithItemsV1 } from '@core/models/order-admin';
import { OrdersService } from '@core/services/orders';

const DEFAULT_PAGE_SIZE = 25;

@Component({
  selector: 'yt-orders-list',
  imports: [
    DatePipe,
    FormsModule,
    RouterModule,
    TableModule,
    ButtonModule,
    DatePickerModule,
    TagModule,
    MessageModule,
    SkeletonModule,
    PageHeader
  ],
  templateUrl: './orders-list.html',
  styleUrl: './orders-list.scss'
})
export class OrdersList {
  private readonly ordersAPIs = inject(OrdersService);

  protected readonly page = signal<ServiceResponseV1<OrderWithItemsV1[]> | null>(null);
  protected readonly loading = signal<boolean>(false);
  protected readonly error = signal<boolean>(false);
  protected readonly pageNum = signal<number>(1);
  protected readonly pageSize = signal<number>(DEFAULT_PAGE_SIZE);
  protected readonly startDate = signal<Date | null>(null);
  protected readonly endDate = signal<Date | null>(null);

  protected readonly orders = this.ordersAPIs.orders;
  protected readonly totalRecords = computed<number>(() => this.page()?.paging?.totalSize ?? 0);

  constructor() {
    effect(() => {
      const query: OrderAdminListQuery = {
        pageNum: this.pageNum(),
        pageSize: this.pageSize(),
        start: this.startDate()?.toISOString(),
        end: this.endDate()?.toISOString()
      };
      untracked(() => void this.loadOrders(query));
    });
  }

  protected hasActiveFilters(): boolean {
    return this.startDate() !== null || this.endDate() !== null;
  }

  protected clearFilters(): void {
    this.startDate.set(null);
    this.endDate.set(null);
    this.pageNum.set(1);
  }

  protected applyFilters(): void {
    this.pageNum.set(1);
  }

  protected onPage(event: { first: number; rows: number }): void {
    this.pageNum.set(Math.floor(event.first / event.rows) + 1);
    this.pageSize.set(event.rows);
  }

  private async loadOrders(query: OrderAdminListQuery): Promise<void> {
    this.loading.set(true);
    this.error.set(false);
    try {
      const response = await firstValueFrom(this.ordersAPIs.getOrdersList(query));
      this.page.set(response);
    } catch {
      this.error.set(true);
    } finally {
      this.loading.set(false);
    }
  }
}
